import { isPositiveUsdt, microToUsdt } from '@/utils/money';
import type {
  CreateTronEnergyOrderResult,
  EnableTronEnergyChallengeResult,
  TronEnergyConfig,
  TronEnergyCostEstimate,
  TronEnergyOrder,
  TronEnergyOrderStatus,
  TronEnergyOrdersResult,
  TronEnergyServiceStatus,
  TronEnergySummary,
} from './tronEnergy';

export type EnergyRentalAction = 'enable' | 'rent';

export type ApiEnergyRentalStatus = 'PROCESSING' | 'SUCCESS' | 'FAILED';

export interface ApiEnergyRentalOrderItem {
  orderNo?: string;
  appId: string;
  receiverAddress: string;
  status: ApiEnergyRentalStatus;
  upstreamOrderId?: string | null;
  upstreamStatus?: string | null;
  upstreamConfirmStatus?: string | null;
  creditsReserved?: number;
  usdtReservedMicro?: number;
  creditsCharged: number;
  usdtCharged?: string;
  usdtChargedMicro: number;
  energyAmount: number;
  completedEnergyAmount?: number;
  durationHours: number;
  paidAt: number | null;
  delegatedAt: number | null;
  expiresAt: number | null;
  failReason?: string | null;
  source: 'console' | 'api';
  createdAt: number;
  updatedAt: number;
  gatewayOrderNo?: string;
  appName?: string;
  rentalOrderNo?: string | null;
}

export interface ApiEnergyRentalStatusData {
  enabled: boolean;
  enabledAt: number | null;
  minEnergyAmount: number;
  minDurationHours: number;
}

export interface ApiEnergyRentalOverviewData {
  remainingCredits?: number;
  availableCredits?: number;
  accountBalanceUsdt?: string;
  availableUsdt?: string;
  availableUsdtMicro?: number;
  period?: string;
  periodStart?: number;
  periodEnd?: number;
  monthCreditsConsumed?: number;
  creditsCharged?: number;
  monthUsdtConsumed?: string;
  monthUsdtConsumedMicro?: number;
  usdtChargedMicro?: number;
  usdtChargeMicro?: number;
  monthEnergyRented?: number;
  energyAmount?: number;
  monthOrderCount?: number;
  orderCount?: number;
}

export interface ApiEnergyRentalQuoteData {
  creditsRequired: number;
  planCode?: string;
  creditsPerUsdt?: number;
  usdtPerCredit?: string;
  creditsToDeduct: number;
  usdtToDeduct?: string;
  usdtToDeductMicro?: number;
  availableCredits?: number;
  availableUsdt?: string;
  availableUsdtMicro?: number;
  sufficient: boolean;
  pricingBaseEnergy?: number;
  pricingBaseCredits?: number;
  creditsPerEnergyDisplay?: string;
  pricingIncrementNumerator?: number;
  pricingIncrementDenominator?: number;
}

export interface ApiEnergyRentalOrdersData {
  items?: ApiEnergyRentalOrderItem[];
  pagination?: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
  list?: ApiEnergyRentalOrderItem[];
  page?: number;
  pageSize?: number;
  total?: number;
}

export type ApiEnergyRentalCreateOrderData = Partial<ApiEnergyRentalOrderItem> & {
  items?: ApiEnergyRentalOrderItem[];
  list?: ApiEnergyRentalOrderItem[];
  remainingCredits?: number;
  accountBalanceUsdt?: string;
};

export function mapOrderStatus(status?: string | null): TronEnergyOrderStatus {
  switch ((status || '').toUpperCase()) {
    case 'PROCESSING':
      return 'processing';
    case 'SUCCESS':
      return 'success';
    case 'FAILED':
      return 'failed';
    default:
      return 'processing';
  }
}

export function toApiOrderStatus(status?: TronEnergyOrderStatus | ''): ApiEnergyRentalStatus | undefined {
  switch (status) {
    case 'processing':
      return 'PROCESSING';
    case 'success':
      return 'SUCCESS';
    case 'failed':
      return 'FAILED';
    default:
      return undefined;
  }
}

function resolvePaidPoints(item: ApiEnergyRentalOrderItem): number {
  const status = mapOrderStatus(item.status);
  if (status === 'processing') {
    return item.creditsReserved ?? 0;
  }
  return item.creditsCharged ?? 0;
}

function resolveUsdtFromMicro(micro?: number): string | null {
  if (micro == null) return null;
  const usdt = microToUsdt(micro);
  return isPositiveUsdt(usdt) ? usdt : null;
}

function resolvePaidUsdt(item: ApiEnergyRentalOrderItem): string {
  const status = mapOrderStatus(item.status);
  if (status === 'processing') {
    return item.usdtReservedMicro != null ? microToUsdt(item.usdtReservedMicro) : '0';
  }
  if (isPositiveUsdt(item.usdtCharged || '0')) {
    return item.usdtCharged || '0';
  }
  const chargedUsdt = resolveUsdtFromMicro(item.usdtChargedMicro);
  if (chargedUsdt) {
    return chargedUsdt;
  }
  // SUCCESS mixed payment may keep the USDT portion on usdtReservedMicro
  // while usdtChargedMicro stays 0. Do not use reserved after FAILED.
  if (status === 'success') {
    return resolveUsdtFromMicro(item.usdtReservedMicro) ?? '0';
  }
  return '0';
}

function resolveOrderNo(item: ApiEnergyRentalOrderItem): string {
  const orderNo = item.gatewayOrderNo || item.orderNo || item.rentalOrderNo;
  if (!orderNo) {
    throw new Error('Order number missing from energy rental response');
  }
  return orderNo;
}

export function mapOrderItem(item: ApiEnergyRentalOrderItem): TronEnergyOrder {
  return {
    orderNo: resolveOrderNo(item),
    rechargeAddress: item.receiverAddress,
    status: mapOrderStatus(item.status),
    paidPoints: resolvePaidPoints(item),
    paidUsdt: resolvePaidUsdt(item),
    rentedEnergy: item.energyAmount,
    completedEnergy: item.completedEnergyAmount ?? 0,
    durationHours: item.durationHours,
    rentDuration: `${item.durationHours}小时`,
    paidTime: item.paidAt,
    delegateTime: item.delegatedAt,
    expireTime: item.expiresAt,
  };
}

export function mapServiceStatus(data: ApiEnergyRentalStatusData, appId: string): TronEnergyServiceStatus {
  return {
    appId,
    enabled: data.enabled,
    enabledAt: data.enabledAt,
  };
}

export function mapConfig(data: ApiEnergyRentalStatusData): TronEnergyConfig {
  const minEnergy = data.minEnergyAmount;
  return {
    minEnergy,
    energyPresets: [65000, 131000, 650000, 1000000],
    durationPresets: [{ label: '1小时', value: 1, unit: 'hour' }],
    durationUnits: ['hour'],
    pointsPerEnergy: '1.3846',
    pointsPerUsdt: 61224,
    docsUrl: import.meta.env.VITE_TRON_ENERGY_DOCS_URL || '',
    fixedDurationHours: data.minDurationHours,
  };
}

function resolveOverviewUsdt(data: ApiEnergyRentalOverviewData): string {
  if (data.accountBalanceUsdt != null && data.accountBalanceUsdt !== '') {
    return data.accountBalanceUsdt;
  }
  if (data.availableUsdt != null && data.availableUsdt !== '') {
    return data.availableUsdt;
  }
  if (data.availableUsdtMicro != null) {
    return microToUsdt(data.availableUsdtMicro);
  }
  return '0';
}

function resolveMonthUsdt(data: ApiEnergyRentalOverviewData): string {
  if (data.monthUsdtConsumed != null && data.monthUsdtConsumed !== '') {
    return data.monthUsdtConsumed;
  }
  const micro =
    data.monthUsdtConsumedMicro ?? data.usdtChargedMicro ?? data.usdtChargeMicro;
  if (micro != null) {
    return microToUsdt(micro);
  }
  return '0';
}

export function mapSummary(data: ApiEnergyRentalOverviewData): TronEnergySummary {
  return {
    remainingPoints: data.remainingCredits ?? data.availableCredits ?? 0,
    accountUsdtBalance: resolveOverviewUsdt(data),
    monthSpentPoints: data.creditsCharged ?? data.monthCreditsConsumed ?? 0,
    monthSpentUsdt: resolveMonthUsdt(data),
    monthRentedEnergy: data.energyAmount ?? data.monthEnergyRented ?? 0,
    monthOrderCount: data.orderCount ?? data.monthOrderCount ?? 0,
  };
}

function resolveQuoteUsdt(data: ApiEnergyRentalQuoteData): string {
  if (data.usdtToDeduct != null && data.usdtToDeduct !== '') {
    return data.usdtToDeduct;
  }
  if (data.usdtToDeductMicro != null) {
    return microToUsdt(data.usdtToDeductMicro);
  }
  return '0';
}

export function mapQuote(data: ApiEnergyRentalQuoteData): TronEnergyCostEstimate {
  return {
    requiredPoints: data.creditsRequired,
    pointsToCharge: data.creditsToDeduct,
    usdtToCharge: resolveQuoteUsdt(data),
    affordable: data.sufficient,
  };
}

export function mapOrdersResult(data: ApiEnergyRentalOrdersData): TronEnergyOrdersResult {
  const items = data.items ?? data.list;
  const total = data.pagination?.total ?? data.total;
  const page = data.pagination?.page ?? data.page;
  const pageSize = data.pagination?.pageSize ?? data.pageSize;
  if (!items || total == null || page == null || pageSize == null) {
    throw new Error('Invalid energy rental orders response');
  }

  return {
    items: items.map(mapOrderItem),
    total,
    page,
    limit: pageSize,
  };
}

function extractCreatedOrder(data: ApiEnergyRentalCreateOrderData): ApiEnergyRentalOrderItem | undefined {
  const wrappedOrder = data.items?.[0] ?? data.list?.[0];
  if (wrappedOrder) {
    return wrappedOrder;
  }
  if ((data.gatewayOrderNo || data.orderNo || data.rentalOrderNo) && data.receiverAddress && data.status) {
    return data as ApiEnergyRentalOrderItem;
  }
  return undefined;
}

export function mapCreateOrderResult(data: ApiEnergyRentalCreateOrderData): CreateTronEnergyOrderResult {
  const order = extractCreatedOrder(data);
  if (!order) {
    throw new Error('Order item missing from create order response');
  }

  return {
    order: mapOrderItem(order),
    remainingPoints: data.remainingCredits,
    accountUsdtBalance: data.accountBalanceUsdt,
  };
}

export function mapOrderDetail(data: ApiEnergyRentalCreateOrderData): TronEnergyOrder {
  const order = extractCreatedOrder(data);
  if (!order) {
    throw new Error('Order item missing from energy rental detail response');
  }
  return mapOrderItem(order);
}

export function mapChallenge(data: {
  challengeId: string;
  message: string;
  expiresAt: number;
}): EnableTronEnergyChallengeResult {
  return {
    challengeId: data.challengeId,
    message: data.message,
    expiresAt: data.expiresAt,
  };
}
